import React, { useState } from 'react';
import { Sparkle, X, ArrowRight, Check } from '@phosphor-icons/react';

/**
 * Reusable Smart Fill modal — turns free-form text into structured field values
 * via POST /api/meals/smart-fill. Used across Eater editor, Food Profile staff
 * section, Allergies field, and Notes-for-AI textarea.
 *
 * Props:
 *   api          — axios instance with auth
 *   target       — "eater_profile" | "staff_config" | "allergies" | "notes_polish"
 *   title        — modal heading
 *   placeholder  — textarea placeholder
 *   examples     — array of example one-liners shown as tap-to-fill chips
 *   context      — optional object passed to backend (e.g., {existing_allergies})
 *   showMergeMode — when true, modal shows Merge/Replace toggle (for allergies)
 *   onApply      — ({parsed, mode: 'merge'|'replace'}) => void; caller decides how to apply
 *   onClose      — ()=>void
 */
export default function SmartFillModal({
  api, target, title, placeholder, examples = [], context = {},
  showMergeMode = false, onApply, onClose,
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState(null);
  const [mode, setMode] = useState('merge'); // merge | replace

  const handleParse = async () => {
    setError('');
    if (text.trim().length < 5) { setError('Please type at least a few words'); return; }
    setBusy(true);
    try {
      const r = await api.post('/meals/smart-fill', { target, text, context });
      setParsed(r.data?.parsed || {});
    } catch (e) {
      setError(e.response?.data?.detail || 'Smart fill failed — please retry');
    }
    setBusy(false);
  };

  const handleApply = () => {
    if (!parsed) return;
    onApply && onApply({ parsed, mode });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid={`smart-fill-modal-${target}`}
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-purple-400" />
            <h3 className="text-sm font-bold text-white">{title}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="sf-error">{error}</div>}

          {!parsed && (
            <>
              <p className="text-[12px] text-neutral-400">
                Describe in plain English (or Hinglish). The AI will extract the right fields — you&apos;ll review before applying.
              </p>
              <textarea rows="5" value={text} onChange={e => setText(e.target.value)}
                placeholder={placeholder}
                data-testid="sf-input"
                className="sf-input" />
              {examples.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Try one</p>
                  <div className="flex flex-wrap gap-1.5">
                    {examples.map((ex, i) => (
                      <button key={i} type="button" onClick={() => setText(ex)}
                        data-testid={`sf-example-${i}`}
                        className="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10">
                        {ex.length > 60 ? ex.slice(0, 60) + '…' : ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={handleParse} disabled={busy || text.trim().length < 5}
                data-testid="sf-parse-btn"
                className="w-full px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #A855F7, #EC4899)', color: 'white' }}>
                {busy ? 'Thinking…' : '✨ Extract fields'}
              </button>
            </>
          )}

          {parsed && (
            <>
              <ParsedPreview parsed={parsed} target={target} />

              {showMergeMode && (
                <div className="rounded-xl p-3 flex items-center justify-between gap-2"
                  style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.20)' }}>
                  <p className="text-[11px] text-neutral-300">How should this be applied?</p>
                  <div className="flex gap-1">
                    <button onClick={() => setMode('merge')} data-testid="sf-mode-merge"
                      className={`px-2.5 py-1 rounded-md text-[10px] font-bold ${mode === 'merge' ? 'bg-purple-500/30 text-purple-100 ring-1 ring-purple-400' : 'bg-white/5 text-neutral-400'}`}>
                      Merge (add)
                    </button>
                    <button onClick={() => setMode('replace')} data-testid="sf-mode-replace"
                      className={`px-2.5 py-1 rounded-md text-[10px] font-bold ${mode === 'replace' ? 'bg-amber-500/30 text-amber-100 ring-1 ring-amber-400' : 'bg-white/5 text-neutral-400'}`}>
                      Replace (overwrite)
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
            Cancel
          </button>
          {parsed && (
            <>
              <button onClick={() => { setParsed(null); setText(''); }}
                data-testid="sf-redo-btn"
                className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
                Redo
              </button>
              <button onClick={handleApply} data-testid="sf-apply-btn"
                className="px-4 py-2 rounded-lg text-white text-xs font-bold inline-flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #A855F7, #EC4899)' }}>
                <Check size={11} weight="bold" /> Apply
              </button>
            </>
          )}
        </div>
        <style>{`.sf-input{width:100%;padding:10px 12px;border-radius:10px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none;resize:vertical}.sf-input:focus{border-color:#A855F7}`}</style>
      </div>
    </div>
  );
}

function ParsedPreview({ parsed, target }) {
  // Render parsed fields as a list of label → value rows.
  const rows = Object.entries(parsed)
    .filter(([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0) && v !== '')
    .map(([k, v]) => ({
      label: k.replace(/_/g, ' '),
      value: Array.isArray(v) ? v : String(v),
    }));

  if (rows.length === 0) {
    return (
      <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.20)' }}
        data-testid="sf-empty-result">
        <p className="text-[12px] text-amber-200">No fields extracted. Try with more detail.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.20)' }}
      data-testid="sf-preview">
      <p className="text-[10px] uppercase tracking-wider font-bold text-purple-300 flex items-center gap-1">
        <ArrowRight size={9} weight="bold" /> Extracted ({rows.length} field{rows.length > 1 ? 's' : ''})
      </p>
      {rows.map(r => (
        <div key={r.label} className="flex items-start gap-2" data-testid={`sf-row-${r.label.replace(/\s+/g, '-')}`}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 min-w-[110px] flex-shrink-0 pt-0.5">{r.label}</p>
          <div className="flex-1 min-w-0">
            {Array.isArray(r.value) ? (
              <div className="flex flex-wrap gap-1">
                {r.value.map((x, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold bg-white/8 text-purple-100">
                    {x}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-white break-words">{r.value}</p>
            )}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-neutral-500 italic pt-1">Tap Apply to add these to the form — you can still edit before saving.</p>
    </div>
  );
}
